import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import PageLayout from '@/components/feature/PageLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import { mangaList } from '@/mocks/manga';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const manga = mangaList.find((m) => m.id === Number(id));

  if (!manga) {
    return (
      <PageLayout>
        <div className="pt-20 min-h-screen flex flex-col items-center justify-center bg-background-50">
          <div className="w-20 h-20 bg-background-200 rounded-full flex items-center justify-center mb-6">
            <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已下架</p>
          <Link
            to="/category/all"
            className="bg-primary-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回漫画列表
          </Link>
        </div>
      </PageLayout>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 虫虫漫画免费在线阅读`,
    description: manga.description,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    datePublished: manga.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '虫虫漫画',
    },
  };

  const formatCount = (n: number): string => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    return n.toLocaleString();
  };

  const relatedManga = mangaList
    .filter((m) => m.id !== manga.id && (m.category === manga.category || m.tags.some((t) => manga.tags.includes(t))))
    .slice(0, 4);

  return (
    <PageLayout>
      <SEOHead
        title={`${manga.title} - 免费在线阅读`}
        description={manga.description.slice(0, 150)}
        keywords={`${manga.title},${manga.author},${manga.category}漫画,免费${manga.category}漫画,虫虫漫画${manga.title}`}
        canonicalUrl={`https://www.chongchongmanga.com/article/${manga.id}`}
        ogType="article"
        ogImage={manga.cover}
        schema={articleSchema}
      />

      <div className="pt-16">
        {/* Breadcrumb */}
        <section className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${manga.category}漫画`, href: `/category/${manga.category === '热血' ? 'rexue' : manga.category === '恋爱' ? 'lianai' : manga.category === '玄幻' ? 'xuanhuan' : manga.category === '搞笑' ? 'gaoxiao' : manga.category === '悬疑' ? 'xuanyi' : manga.category === '科幻' ? 'kehuan' : manga.category === '校园' ? 'xiaoyuan' : manga.category === '武侠' ? 'wuxia' : 'maoxian'}` },
                { label: manga.title },
              ]}
            />
          </div>
        </section>

        {/* Manga Detail Header */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Cover */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manga.cover}
                    alt={`${manga.title}漫画封面`}
                    title={`${manga.title} - 免费在线漫画阅读`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 flex flex-col justify-center">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {manga.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">作者：{manga.author}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 text-sm bg-yellow-50 text-yellow-700 px-2.5 py-1 rounded-full">
                    <i className="ri-star-fill text-yellow-500"></i>
                    {manga.rating}
                  </span>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${
                    manga.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {manga.status}
                  </span>
                  <span className="text-xs text-foreground-400">
                    <i className="ri-eye-line mr-1"></i>{formatCount(manga.views)} 阅读
                  </span>
                  <span className="text-xs text-foreground-400">
                    <i className="ri-heart-line mr-1"></i>{formatCount(manga.favorites)} 收藏
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6 bg-background-100 rounded-lg p-4">
                  {manga.description}
                </p>

                <div className="flex items-center gap-3">
                  <Link
                    to={`/article/${manga.id}`}
                    className="bg-primary-500 text-white px-8 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-share-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-8 md:py-12 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              章节列表（共 {manga.chapters.length} 话）
            </h2>
            <div className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
              {manga.chapters.map((chapter, index) => (
                <Link
                  key={chapter.id}
                  to={`/article/${manga.id}`}
                  className={`flex items-center justify-between p-4 hover:bg-background-100 transition-colors ${
                    index !== 0 ? 'border-t border-background-200' : ''
                  }`}
                >
                  <span className="text-sm text-foreground-800 font-medium truncate flex-1">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 ml-4 whitespace-nowrap">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related Manga */}
        {relatedManga.length > 0 && (
          <section className="py-8 md:py-12 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-shop>
                {relatedManga.map((m) => (
                  <Link
                    key={m.id}
                    to={`/article/${m.id}`}
                    className="group block bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 border border-background-200"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden bg-background-200">
                      <img
                        src={m.cover}
                        alt={m.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-foreground-950/70 text-white text-xs px-1.5 py-0.5 rounded-full">
                        <i className="ri-star-fill text-yellow-400 text-[10px]"></i>
                        <span>{m.rating}</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm truncate">{m.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{m.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </PageLayout>
  );
}